/** Shared domain model for the designer. Kept free of React so it stays testable. */

/** Flux aggregate functions that `aggregateWindow` accepts without a custom `fn`. */
export const AGGREGATES = ['mean', 'median', 'min', 'max', 'sum', 'count', 'last'] as const;

export type Aggregate = (typeof AGGREGATES)[number];

/** A tag key kept on the rolled-up data, and how many distinct values it has. */
export interface TagKey {
  /** Tag key as it appears in the line protocol, e.g. `device_id`. */
  key: string;
  /** Distinct values observed for this key across the fleet. */
  cardinality: number;
  /**
   * Whether the tag survives the rollup. Dropping a tag collapses series and is the
   * single most effective lever against cardinality growth in a rollup bucket.
   */
  retained: boolean;
}

export interface SourceConfig {
  /** Bucket the raw writes land in. */
  bucket: string;
  /** InfluxDB organisation, used in the generated `to()` call. */
  org: string;
  /** Measurement name to roll up. */
  measurement: string;
  /** Field keys to roll up. One series per field per tag combination. */
  fields: string[];
  /** Tag keys and their cardinality. */
  tagKeys: TagKey[];
  /** Raw sample interval, e.g. `10s`. */
  interval: string;
  /** Retention of the raw bucket, e.g. `7d`. `0s` means infinite. */
  retention: string;
}

export interface TierConfig {
  /** Stable identity so React lists and the permalink stay in sync. */
  id: string;
  /** Destination bucket for this tier. */
  bucket: string;
  /** Aggregation window, e.g. `1m`. */
  window: string;
  /** Aggregate functions applied in this tier. Each one produces its own series. */
  aggregates: Aggregate[];
  /** Retention of this tier's bucket. `0s` means infinite. */
  retention: string;
  /** Passed straight to `aggregateWindow(createEmpty:)`. */
  createEmpty: boolean;
}

export interface ScheduleConfig {
  /** Task `every`. Defaults to the tier window when left blank. */
  every: string;
  /** Task `offset` — how long after the window closes the task fires. */
  offset: string;
  /** Optional cron expression. When set it replaces `every` in the task option. */
  cron: string;
  /** How late a write may arrive and still be picked up. Widens the query range. */
  lateTolerance: string;
}

export interface EstimateAssumptions {
  /**
   * Average on-disk bytes per stored point after TSM compression. Highly
   * workload-dependent; exposed so the number is an input, not a hidden constant.
   */
  bytesPerPoint: number;
  /**
   * Horizon in days used to price infinite retention and to draw the growth chart.
   */
  horizonDays: number;
}

export interface DesignConfig {
  source: SourceConfig;
  tiers: TierConfig[];
  schedule: ScheduleConfig;
  assumptions: EstimateAssumptions;
}
